/**
 * Main App Page
 *
 * If the hunt hasn't started yet, shows the welcome screen.
 *
 * If the hunt is in progress, shows the game overview, with links to all of the
 * different portions of the game. Also shows progress bars and points earned
 * for each of those portions.
 */

import React from 'react'
import Link from '../components/link'

import {
  getHuntStatus,
  getTeamCompletedQuestions,
  getCurrentTeamData,
  getTeamCompletedSecondaryQuestions,
  getTeamSecondaryQuestionsMeta,
  getTeamQuestionsMeta,
  TEAM_DATA_UPDATED_EVENT,
} from '../utils/team-data'
import { getSiteData } from '../utils/site'
import LoggedInWrapper from '../components/loggedinWrapper'
import { CompassIllustration } from '../components/images/illustrations'
import AppContext from '../utils/context'
import { getCurrentHuntData } from '../utils/hunt-data'
import { getStaticUrl } from '../utils/helpers'
import YourGameSections from '../components/your-game-sections'

export default class IndexPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      data: {},
      cover_image: getStaticUrl(`/images/hunt_cover.jpg`),
      get_started_text: `Ready to get started?`,
      hunt_status: getHuntStatus(),
      num_questions_complete: getTeamCompletedQuestions().length,
    }
    this.handleTeamDataUpdate = this.handleTeamDataUpdate.bind(this)
  }

  componentDidMount() {
    const data = getCurrentHuntData()
    if (data.hunt !== undefined && data.hunt.cover_photo !== null) {
      this.setState({
        cover_image: data.hunt.cover_photo,
      })
    }
    getSiteData().then(data => {
      if (data && data.get_started_text !== null) {
        this.setState({
          get_started_text: data.get_started_text,
        })
      }
    })
    this.setState(
      {
        data: data,
        hunt_status: getHuntStatus(),
        num_questions_complete: getTeamCompletedQuestions().length,
      },
      () => {
        try {
          this.setLoading(false)
        } catch {
          console.log(`unable to set loading`)
        }
      }
    )
    window.addEventListener(TEAM_DATA_UPDATED_EVENT, this.handleTeamDataUpdate)
    window.addEventListener(`storage`, this.handleTeamDataUpdate)
  }

  componentWillUnmount() {
    window.removeEventListener(TEAM_DATA_UPDATED_EVENT, this.handleTeamDataUpdate)
    window.removeEventListener(`storage`, this.handleTeamDataUpdate)
  }

  handleTeamDataUpdate(event) {
    if (event.type === `storage` && event.key !== `teamData`) {
      return
    }
    this.setState({
      hunt_status: getHuntStatus(),
      num_questions_complete: getTeamCompletedQuestions().length,
    })
  }

  render() {
    const { data, num_questions_complete, hunt_status } = this.state
    // Permanent (no-host) codes also collect name + optional email on /settings/
    // so they appear in the Events "export emails" CSV.
    if (data.hunt == undefined) return <LoggedInWrapper />
    const isGnG = data.hunt.hunt_type == 1

    const team = getCurrentTeamData()

    let q_meta = getTeamQuestionsMeta()
    let num_questions = q_meta.num_questions
    let q_points = q_meta.score
    let q_possible_points = q_meta.possible_points

    let final_q_meta = getTeamSecondaryQuestionsMeta()
    let num_final_questions = final_q_meta.num_questions
    let final_q_points = final_q_meta.score
    let final_q_possible_points = final_q_meta.possible_points
    let num_final_questions_complete = final_q_meta.num_complete

    // check if questions are complete by comparing how many questions there are with the completed ones
    let qCompletedClass = num_questions_complete == num_questions ? `completed` : ``

    // check if there is a maximum amount of points
    if (data.hunt.max_question_points !== undefined && data.hunt.max_question_points > 0) {
      // "complete" means if they've gotten the max amount of points
      qCompletedClass = q_points == q_possible_points ? `completed` : ``
    }

    let finalQCompletedClass = num_final_questions_complete == num_final_questions ? `completed` : ``

    // mystery message variables
    let mm_points = 0
    let mm_possible_points = data.mystery_message !== undefined ? data.mystery_message.point_value : 15
    if (team.mystery_message_answers && team.mystery_message_answers.points !== undefined) {
      mm_points = team.mystery_message_answers.points
    }
    let mmCompletedClass = mm_points == mm_possible_points ? `completed` : ``

    // emergency message variables
    let em_points = 0
    let em_possible_points = data.emergency_message !== undefined ? data.emergency_message.point_value : 15
    if (team.emergency_message_answers && team.emergency_message_answers.points !== undefined) {
      em_points = team.emergency_message_answers.points
    }
    let emCompletedClass = em_points == em_possible_points ? `completed` : ``

    let indexContent = (
      <div className="pb-5 mb-3">
        <div className="site-sub-header px-4 py-3">
          <div className="row">
            <div className="col text-center">
              <h1 className={`title h5 text-uppercase mb-0`}>Your Game</h1>
            </div>
          </div>
        </div>

        <YourGameSections
          data={data}
          hunt_status={hunt_status}
          isGnG={isGnG}
          team={team}
          num_questions_complete={num_questions_complete}
          num_questions={num_questions}
          q_points={q_points}
          q_possible_points={q_possible_points}
          qCompletedClass={qCompletedClass}
          num_final_questions={num_final_questions}
          num_final_questions_complete={num_final_questions_complete}
          final_q_points={final_q_points}
          final_q_possible_points={final_q_possible_points}
          finalQCompletedClass={finalQCompletedClass}
          mm_points={mm_points}
          mm_possible_points={mm_possible_points}
          mmCompletedClass={mmCompletedClass}
          em_points={em_points}
          em_possible_points={em_possible_points}
          emCompletedClass={emCompletedClass}
        />
      </div>
    )

    if (this.state.hunt_status == `pending`) {
      indexContent = (
        <div className={`hunt-pending`}>
          <div
            className={`row text-center justify-content-center align-items-center hunt-cover`}
            style={{
              backgroundColor: `#444`,
              color: `white`,
              backgroundPosition: `center`,
              backgroundSize: `cover`,
              backgroundImage: `url(${this.state.cover_image})`,
              paddingTop: `40px`,
              paddingBottom: `40px`,
            }}>
            <div className={`col-10`} style={{ minHeight: `90px` }}>
              <h2 className={`text-center text-uppercase h2 title`}>
                {data.hunt.display_title.split(`\n`).map((item, key) => (
                  <span key={key} style={{ display: `block` }}>
                    {item}
                  </span>
                ))}
              </h2>
            </div>
          </div>
          <div className={`hunt-pending-illustration`}>
            <CompassIllustration />
          </div>

          <div className={`card mx-4 hunt-pending-card`}>
            <div className={`card-body`}>
              <h6 className={`subtitle h3 text-uppercase text-center mb-4 mt-3`}>{this.state.get_started_text}</h6>

              {data.hunt.enable_qr_code !== undefined && data.hunt.enable_qr_code && (
                <div>
                  <h6 className={`h6 mb-3 text-center`}>Share this QR code with your teammates:</h6>
                  <div className={`text-center`} dangerouslySetInnerHTML={{ __html: team.qr_code }} />
                  <p className="text-center">
                    Or go to fun.watsonadventures.com and enter{` `}
                    <span className="h5 fw-bold font-weight-bold">{team.access_code}</span>.
                  </p>
                </div>
              )}

              <div className={`mx-0`}>
                <Link className={`btn btn-block btn-secondary btn-arrow py-3 my-3`} to={`/settings/`}>
                  Continue
                </Link>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <AppContext.Consumer>
        {({ setLoading }) => {
          this.setLoading = setLoading
          return <LoggedInWrapper>{indexContent}</LoggedInWrapper>
        }}
      </AppContext.Consumer>
    )
  }
}
